import React, { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { collection, query, where, onSnapshot, doc, updateDoc, arrayUnion, arrayRemove, Timestamp, limit } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from './AuthContext';

const PeekNotificationContext = createContext(null);

export const usePeekNotifications = () => {
    const context = useContext(PeekNotificationContext);
    if (!context) {
        throw new Error('usePeekNotifications must be used within a PeekNotificationProvider');
    }
    return context;
};

// 30 days in milliseconds for auto-archive
const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;

/**
 * Peek Notification Provider
 * Listens to Firestore 'notifications' collection filtered by user role
 * Provides real-time notifications for cross-role workflow events
 * 
 * Enhanced with:
 * - 30-day auto-archiving policy
 * - Full notification state management for dedicated Notifications Dashboard
 * - Snooze and unsnooze functionality
 * - Action type classification and situational awareness
 * - Top-5 priority surfaced peek panel
 */
export const PeekNotificationProvider = ({ children }) => {
    const { user, userProfile } = useAuth();
    const [notifications, setNotifications] = useState([]); // Active unread, unarchived, unsnoozed
    const [allNotifications, setAllNotifications] = useState([]); // All docs for dashboard
    const [isLoading, setIsLoading] = useState(true);
    const [isPanelOpen, setIsPanelOpen] = useState(false);
    const [snoozedIds, setSnoozedIds] = useState(new Map()); // Map of notificationId -> snoozeUntil timestamp
    const audioRef = useRef(null);
    const lastNotificationCountRef = useRef(0);

    // Get current user's role
    const userRole = userProfile?.role || null;
    const userId = user?.uid || null;

    // Real-time listener for notifications
    useEffect(() => {
        if (!userRole || !userId) {
            setNotifications([]);
            setAllNotifications([]);
            setIsLoading(false);
            return;
        }

        console.log(`🔔 [Notifications] Setting up listener for role: ${userRole}`);

        let singleRoleData = [];
        let arrayRolesData = [];

        // Query 1: Notifications with target_role (single role, exact match)
        const qSingleRole = query(
            collection(db, 'notifications'),
            where('target_role', '==', userRole),
            limit(150)
        );

        // Query 2: Notifications with target_roles array (contains user's role)
        const qRolesArray = query(
            collection(db, 'notifications'),
            where('target_roles', 'array-contains', userRole),
            limit(150)
        );

        // Query 3: For matron, also subscribe to nurse-targeted notifications
        const isMatron = userRole === 'matron';
        const qNurseNotifs = isMatron ? query(
            collection(db, 'notifications'),
            where('target_role', '==', 'nurse'),
            limit(150)
        ) : null;

        // Helper to merge and update notifications
        let nurseRoleData = []; // For matron's nurse notifications
        const mergeAndUpdate = () => {
            const allDocs = [...singleRoleData, ...arrayRolesData, ...nurseRoleData];

            // Deduplicate by ID
            const notifMap = new Map();
            allDocs.forEach(n => {
                if (!notifMap.has(n.id)) {
                    notifMap.set(n.id, n);
                }
            });

            const now = Date.now();

            // Map and classify all documents
            const processedAll = Array.from(notifMap.values()).map(n => {
                const createdAtDate = n.created_at?.toDate?.() || (n.created_at ? new Date(n.created_at) : new Date());
                const createdAtMs = createdAtDate.getTime();
                const isRead = Boolean((n.read_by || []).includes(userId));
                const snoozeUntil = snoozedIds.get(n.id);
                const isSnoozed = Boolean(snoozeUntil && now < snoozeUntil);
                const isAutoArchived = (now - createdAtMs) > THIRTY_DAYS_MS;
                const isArchived = Boolean(n.is_archived || isAutoArchived);
                
                let isExpired = false;
                if (n.expires_at) {
                    const expiresAt = n.expires_at?.toDate?.() || new Date(n.expires_at);
                    if (now > expiresAt.getTime()) isExpired = true;
                }

                // Resolve patient_name robustly across all modules (Doctor, Nurse, Lab, Pharmacy)
                let resolvedPatientName = n.patient_name || n.patientName || n.action_context?.patient_name || n.action_context?.patientName || null;

                // If missing, extract from message (e.g., "Ajibola Sulaiman has paid...", "Ajibola Sulaiman - ...")
                if (!resolvedPatientName && n.message) {
                    const nameMatch = n.message.match(/^([A-Z][a-zA-Z'.\s]+?)(?:\s+has\s+|\s*[-–—]\s*|\s*\(MRN:|\s+admitted\s+|\s+added\s+|\s+is\s+seeing)/i);
                    if (nameMatch && nameMatch[1] && nameMatch[1].trim().length > 2) {
                        const candidate = nameMatch[1].trim();
                        if (!candidate.toLowerCase().includes('invoice') && !candidate.toLowerCase().includes('prescription') && !candidate.toLowerCase().includes('dr.')) {
                            resolvedPatientName = candidate;
                        }
                    }
                }

                // Resolve patient_mrn
                let resolvedPatientMrn = n.patient_mrn || n.patientMrn || n.action_context?.patient_mrn || n.action_context?.patientMrn || null;
                if (!resolvedPatientMrn && n.message) {
                    const mrnMatch = n.message.match(/MRN[:\s-]+([A-Za-z0-9-]+)/i);
                    if (mrnMatch && mrnMatch[1]) {
                        resolvedPatientMrn = mrnMatch[1].trim();
                    }
                }

                return {
                    ...n,
                    patient_name: resolvedPatientName,
                    patient_mrn: resolvedPatientMrn,
                    created_at: createdAtDate,
                    isRead,
                    isSnoozed,
                    snoozeUntil,
                    isArchived,
                    isExpired,
                    action_type: n.action_type || inferActionType(n.type)
                };
            });

            // Active list: unread, not snoozed, not expired, not archived
            const activeList = processedAll.filter(n => !n.isRead && !n.isSnoozed && !n.isArchived && !n.isExpired);

            // Play sound for new urgent notifications
            if (activeList.length > lastNotificationCountRef.current) {
                const hasUrgentNew = activeList.some(n => 
                    n.priority === 'urgent' || n.priority === 'emergency' || n.priority === 'stat' || n.priority === 'critical'
                );
                if (hasUrgentNew && audioRef.current) {
                    try {
                        audioRef.current.play().catch(() => {});
                    } catch (e) {
                        // Ignore audio errors
                    }
                }
            }
            lastNotificationCountRef.current = activeList.length;

            console.log(`🔔 [Notifications] Merged ${activeList.length} active (Total: ${processedAll.length})`);
            setNotifications(activeList);
            setAllNotifications(processedAll);
            setIsLoading(false);
        };

        // Subscribe to single role query
        const unsubscribe1 = onSnapshot(
            qSingleRole,
            (snapshot) => {
                singleRoleData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                mergeAndUpdate();
            },
            (error) => {
                console.error('❌ [Notifications] Single role query error:', error);
                setIsLoading(false);
            }
        );

        // Subscribe to array-contains query
        const unsubscribe2 = onSnapshot(
            qRolesArray,
            (snapshot) => {
                arrayRolesData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                mergeAndUpdate();
            },
            (error) => {
                console.error('❌ [Notifications] Array-contains query error:', error);
                setIsLoading(false);
            }
        );

        // Subscribe to nurse notifications for matron
        let unsubscribe3 = null;
        if (qNurseNotifs) {
            unsubscribe3 = onSnapshot(
                qNurseNotifs,
                (snapshot) => {
                    nurseRoleData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                    mergeAndUpdate();
                },
                (error) => {
                    console.error('❌ [Notifications] Nurse role query error:', error);
                }
            );
        }

        return () => {
            unsubscribe1();
            unsubscribe2();
            if (unsubscribe3) unsubscribe3();
        };
    }, [userRole, userId, snoozedIds]);

    // Infer action type from notification type
    const inferActionType = (type) => {
        const workflowTypes = ['payment_confirmed', 'queue_new', 'patient_registered', 'triage_ready', 'admission_created', 'discharge_pending'];
        const resumeTypes = ['lab_results', 'medication_dispensed', 'consultation_complete', 'vitals_recorded'];
        const viewTypes = ['billing_new', 'billing_request', 'invoice_created', 'prescription', 'system'];
        
        if (workflowTypes.includes(type)) return 'workflow';
        if (resumeTypes.includes(type)) return 'resume';
        if (viewTypes.includes(type)) return 'view';
        return 'view'; // Default
    };

    // Dismiss / Mark as read
    const dismissNotification = useCallback(async (notificationId) => {
        if (!userId) return;
        try {
            const notifRef = doc(db, 'notifications', notificationId);
            await updateDoc(notifRef, {
                read_by: arrayUnion(userId)
            });
            console.log(`✅ [Notifications] Marked read: ${notificationId}`);
        } catch (error) {
            console.error('❌ [Notifications] Failed to mark read:', error);
        }
    }, [userId]);

    // Mark as unread
    const markAsUnread = useCallback(async (notificationId) => {
        if (!userId) return;
        try {
            const notifRef = doc(db, 'notifications', notificationId);
            await updateDoc(notifRef, {
                read_by: arrayRemove(userId)
            });
            console.log(`✅ [Notifications] Marked unread: ${notificationId}`);
        } catch (error) {
            console.error('❌ [Notifications] Failed to mark unread:', error);
        }
    }, [userId]);

    // Archive notification explicitly
    const archiveNotification = useCallback(async (notificationId) => {
        try {
            const notifRef = doc(db, 'notifications', notificationId);
            await updateDoc(notifRef, {
                is_archived: true
            });
            console.log(`📦 [Notifications] Archived: ${notificationId}`);
        } catch (error) {
            console.error('❌ [Notifications] Failed to archive:', error);
        }
    }, []);

    // Dismiss all visible notifications
    const dismissAll = useCallback(async () => {
        if (!userId || notifications.length === 0) return;
        try {
            const promises = notifications.map(n => {
                const notifRef = doc(db, 'notifications', n.id);
                return updateDoc(notifRef, { read_by: arrayUnion(userId) });
            });
            await Promise.all(promises);
            console.log(`✅ [Notifications] Dismissed all ${notifications.length} notifications`);
        } catch (error) {
            console.error('❌ [Notifications] Failed to dismiss all:', error);
        }
    }, [notifications, userId]);

    // Snooze a notification
    const snoozeNotification = useCallback((notificationId, minutes = 15) => {
        const snoozeUntil = Date.now() + (minutes * 60 * 1000);
        setSnoozedIds(prev => {
            const newMap = new Map(prev);
            newMap.set(notificationId, snoozeUntil);
            return newMap;
        });
        console.log(`😴 [Notifications] Snoozed ${notificationId} for ${minutes}m`);
    }, []);

    // Unsnooze a notification
    const unsnoozeNotification = useCallback((notificationId) => {
        setSnoozedIds(prev => {
            const newMap = new Map(prev);
            newMap.delete(notificationId);
            return newMap;
        });
        console.log(`⏰ [Notifications] Unsnoozed ${notificationId}`);
    }, []);

    // Toggle panel
    const togglePanel = useCallback(() => {
        setIsPanelOpen(prev => !prev);
    }, []);

    const closePanel = useCallback(() => {
        setIsPanelOpen(false);
    }, []);

    // Unread count (active)
    const unreadCount = notifications.length;

    // Derived lists for Dashboard
    const snoozedNotifications = useMemo(() => 
        allNotifications.filter(n => n.isSnoozed && !n.isArchived),
        [allNotifications]
    );

    const archivedNotifications = useMemo(() => 
        allNotifications.filter(n => n.isArchived || n.isRead),
        [allNotifications]
    );

    // Count by action type
    const actionCounts = useMemo(() => ({
        workflow: notifications.filter(n => n.action_type === 'workflow').length,
        resume: notifications.filter(n => n.action_type === 'resume').length,
        view: notifications.filter(n => n.action_type === 'view').length,
        acknowledge: notifications.filter(n => n.action_type === 'acknowledge').length
    }), [notifications]);

    // Highest priority for bell glow
    const highestPriority = useMemo(() => {
        if (notifications.length === 0) return null;

        const hasEmergency = notifications.some(n =>
            n.priority === 'emergency' ||
            n.priority === 'stat' ||
            n.priority === 4 ||
            n.priority === 'critical'
        );
        if (hasEmergency) return 'emergency';

        const hasUrgent = notifications.some(n =>
            n.priority === 'urgent' ||
            n.priority === 3 ||
            n.priority === 'high'
        );
        if (hasUrgent) return 'urgent';

        if (actionCounts.workflow > 0) return 'action';

        return 'normal';
    }, [notifications, actionCounts]);

    return (
        <PeekNotificationContext.Provider
            value={{
                notifications,
                allNotifications,
                snoozedNotifications,
                archivedNotifications,
                unreadCount,
                actionCounts,
                isLoading,
                isPanelOpen,
                highestPriority,
                togglePanel,
                closePanel,
                dismissNotification,
                markAsRead: dismissNotification,
                markAsUnread,
                archiveNotification,
                dismissAll,
                snoozeNotification,
                unsnoozeNotification
            }}
        >
            {children}
        </PeekNotificationContext.Provider>
    );
};

export default PeekNotificationProvider;
